import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Bed,
  Bath,
  Ruler,
  Calendar,
  Heart,
  Mail,
  Phone,
  Share2,
  Star,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Property } from "@/types/types";
import { 
  getPropertyById, 
  incrementPropertyViews, 
  getSimilarProperties 
} from "@/lib/property-service";
import { addRecentlyViewed } from "@/lib/recently-viewed";
import { formatNaira, cn } from "@/lib/utils";
import { useFavorites } from "@/hooks/use-favorites";
import { PropertyEnquiryDialog } from "@/components/property/property-enquiry-dialog";
import PropertyReviews from "@/components/property/property-reviews";
import { ReviewForm } from "@/components/reviews/review-form";
import { useAuth } from "@/components/auth-provider";
import PropertyCard from "@/components/listings/property-card";

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [similarProperties, setSimilarProperties] = useState<Property[]>([]);
  const { toggleFavorite, isFavorite } = useFavorites();

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  // Track view and load similar properties
  useEffect(() => {
    if (!property) return;

    // Track view after 5 seconds (debounced)
    const viewTimer = setTimeout(() => {
      incrementPropertyViews(property.id);
    }, 5000);

    // Track in recently viewed
    addRecentlyViewed({
      id: property.id,
      title: property.title,
      address: property.address,
      rent: property.rent,
      image: property.image || property.imageUrls?.[0],
    });

    // Load similar properties
    loadSimilarProperties();

    return () => clearTimeout(viewTimer);
  }, [property?.id]);

  async function loadSimilarProperties() {
    if (!property) return;
    
    try {
      const similar = await getSimilarProperties(property.id, 4);
      setSimilarProperties(similar);
    } catch (error) {
      console.error("Error loading similar properties:", error);
    }
  }

  async function loadProperty() {
    if (!id) return;

    try {
      setLoading(true);
      const propertyData = await getPropertyById(id);

      // Only show approved properties to public
      if (!propertyData || propertyData.approvalStatus !== "approved") {
        toast.error("Property not found or no longer available");
        navigate("/properties");
        return;
      }

      setProperty(propertyData);
    } catch (error) {
      console.error("Error loading property:", error);
      toast.error("Failed to load property details");
      navigate("/properties");
    } finally {
      setLoading(false);
    }
  }

  const handleFavoriteClick = async () => {
    if (!property) return;
    await toggleFavorite(property.id);
  };

  const handleShare = async () => {
    if (navigator.share && property) {
      try {
        await navigator.share({
          title: property.title,
          text: `Check out this property: ${property.title}`,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or share not supported
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard");
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-NG", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-8">
          <div className="h-96 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Property not found</h2>
          <Link to="/properties">
            <Button className="mt-4">Back to Listings</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images =
    property.imageUrls && property.imageUrls.length > 0
      ? property.imageUrls
      : property.image
      ? [property.image]
      : ["/placeholder.svg?height=600&width=800"];

  const favorited = isFavorite(property.id);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/properties")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Listings
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Image Gallery */}
        <div className="grid gap-4 mb-8">
          <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
            <img
              src={images[selectedImageIndex]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            {property.status === "vacant" && (
              <Badge className="absolute top-4 left-4 bg-green-500">
                Available Now
              </Badge>
            )}
          </div>

          {images.length > 1 && (
            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={cn(
                    "relative h-20 rounded-lg overflow-hidden border-2 transition-all",
                    selectedImageIndex === index
                      ? "border-primary"
                      : "border-transparent hover:border-muted-foreground"
                  )}
                >
                  <img
                    src={img}
                    alt={`${property.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h1 className="text-3xl font-bold">{property.title}</h1>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleFavoriteClick}
                  >
                    <Heart
                      className={cn(
                        "h-5 w-5 transition-colors",
                        favorited
                          ? "fill-red-500 text-red-500"
                          : "text-gray-600 hover:text-red-500"
                      )}
                    />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleShare}>
                    <Share2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center text-muted-foreground mb-4">
                <MapPin className="h-5 w-5 mr-2" />
                <span className="text-lg">{property.address}</span>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <Badge variant="outline" className="text-base px-3 py-1">
                  {property.type}
                </Badge>
                {property.averageRating && property.averageRating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">
                      {property.averageRating}
                    </span>
                    <span className="text-muted-foreground">
                      ({property.reviewCount} reviews)
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">
                  {formatNaira(property.rent)}
                </span>
                <span className="text-muted-foreground text-lg">/month</span>
              </div>
            </div>

            <Separator />

            {/* Key Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Bed className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">{property.bedrooms}</div>
                  <div className="text-sm text-muted-foreground">Bedrooms</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Bath className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">{property.bathrooms}</div>
                  <div className="text-sm text-muted-foreground">Bathrooms</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Ruler className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">{property.area}</div>
                  <div className="text-sm text-muted-foreground">sqm</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold capitalize">
                    {property.structure}
                  </div>
                  <div className="text-sm text-muted-foreground">Type</div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">
                About this property
              </h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {property.description || "No description available."}
              </p>
            </div>

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <>
                <Separator />
                <div>
                  <h2 className="text-2xl font-semibold mb-4">Amenities</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Multi-unit Building Info */}
            {property.structure === "multi-unit" && property.units && (
              <>
                <Separator />
                <div>
                  <h2 className="text-2xl font-semibold mb-4">
                    Building Information
                  </h2>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {property.totalFloors && (
                      <div>
                        <span className="text-muted-foreground">
                          Total Floors:
                        </span>
                        <span className="ml-2 font-semibold">
                          {property.totalFloors}
                        </span>
                      </div>
                    )}
                    {property.totalUnits && (
                      <div>
                        <span className="text-muted-foreground">
                          Total Units:
                        </span>
                        <span className="ml-2 font-semibold">
                          {property.totalUnits}
                        </span>
                      </div>
                    )}
                  </div>

                  {property.buildingFacilities &&
                    property.buildingFacilities.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">
                          Building Facilities
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {property.buildingFacilities.map(
                            (facility, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <div className="h-2 w-2 rounded-full bg-primary" />
                                <span>{facility}</span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </>
            )}

            {/* Lease Terms */}
            <Separator />
            <div>
              <h2 className="text-2xl font-semibold mb-4">Lease Terms</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {property.leaseDuration && (
                  <div>
                    <span className="text-muted-foreground">
                      Lease Duration:
                    </span>
                    <span className="ml-2 font-semibold">
                      {property.leaseDuration} months
                    </span>
                  </div>
                )}
                {property.securityDeposit && (
                  <div>
                    <span className="text-muted-foreground">
                      Security Deposit:
                    </span>
                    <span className="ml-2 font-semibold">
                      {formatNaira(property.securityDeposit)}
                    </span>
                  </div>
                )}
                {property.paymentTerms && (
                  <div>
                    <span className="text-muted-foreground">
                      Payment Terms:
                    </span>
                    <span className="ml-2 font-semibold capitalize">
                      {property.paymentTerms}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Listed on:</span>
                  <span className="ml-2 font-semibold">
                    {formatDate(property.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Contact Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Interested in this property?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => setIsEnquiryOpen(true)}
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    Contact Landlord
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={() => {
                      // TODO: Implement booking/viewing request
                      toast.info("Booking feature coming soon!");
                    }}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Viewing
                  </Button>
                </div>

                <Separator />

                <div className="text-sm text-muted-foreground space-y-2">
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Call for more details
                  </p>
                  <p className="text-xs">
                    Response time: Usually within 24 hours
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Reviews & Ratings</h2>
                <p className="text-muted-foreground mt-1">
                  See what tenants have to say about this property
                </p>
              </div>
              {user && (
                <Button onClick={() => setIsReviewFormOpen(true)}>
                  <Star className="mr-2 h-4 w-4" />
                  Write a Review
                </Button>
              )}
            </div>
            <PropertyReviews propertyId={property.id} landlordId={property.listerId} />
          </div>
        </div>

        {/* Similar Properties Section */}
        {similarProperties.length > 0 && (
          <div className="py-12 bg-muted/30">
            <div className="max-w-5xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold">Similar Properties</h2>
                <p className="text-muted-foreground mt-1">
                  You might also be interested in these properties
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {similarProperties.map((similarProperty) => (
                  <PropertyCard key={similarProperty.id} listing={similarProperty} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Form Dialog */}
      {property && (
        <ReviewForm
          propertyId={property.id}
          propertyTitle={property.title}
          landlordId={property.listerId}
          open={isReviewFormOpen}
          onOpenChange={setIsReviewFormOpen}
          onSuccess={() => {
            // Reload reviews by triggering re-render
            setProperty({ ...property });
          }}
        />
      )}

      {/* Enquiry Dialog */}
      <PropertyEnquiryDialog
        property={property}
        open={isEnquiryOpen}
        onOpenChange={setIsEnquiryOpen}
      />
    </div>
  );
}
